import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import {
  Bars3Icon,
  ChatBubbleLeftEllipsisIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  StopIcon,
  XMarkIcon
} from "@heroicons/react/20/solid";
import Hls from "hls.js";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatJumpHeightM } from "../utils/Constants";
import { dayTimeCode, getTimingForEvent, saveToPC, toHlsStreamUrl } from "../utils/Utils";

import {
  ChatBubbleLeftEllipsisIcon as ChatBubbleLeftEllipsisIconOutline,
  ClockIcon,
  SignalIcon
} from "@heroicons/react/24/outline";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import ReactPlayer from "react-player";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useLanguage } from "../../context/Language/LanguageContext";
import { storePlaylist } from "../../context/VBLiveAPI/VBLiveAPIAction";
import Share from "../matches/Share";
import Timing from "../matches/Timing";
import { sendShareNotification } from "../utils/emailNotify";
import { myunzip, myzip } from "../utils/zip";
import PlaylistList from "./PlaylistList";

// Once HLS playback falls this many seconds behind the live edge, surface a
// "Go Live" control. hls.js's default initial distance behind the live edge
// (matches liveSyncDuration in the HLS effect below) is the assumed baseline
// before any manual seek; drift beyond that baseline by more than
// kAutoCatchupExtra seconds is auto-corrected (see the HLS effect).
const kLiveDriftThreshold = 20;
const kDefaultLiveDrift = 5;
const kAutoCatchupExtra = 30;

function Playlist() {
  const { t: tr } = useLanguage();
  const navigate = useNavigate();
  const [showPlaylist, setShowPlaylist] = useState(true);
  const [playlists, setPlaylists] = useState(null);
  const [comments, setComments] = useState(null);
  const location = useLocation();
  const {
    playlistFileData,
    filename,
    playlist,
    serverName,
    description,
    initialTags,
  } = location.state;
  const [playlistId, setPlaylistId] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const playerRef = useRef();
  const hlsVideoRef = useRef(null); // raw <video> element used for MediaMTX HLS live streams
  const hlsInstanceRef = useRef(null); // direct hls.js instance for seek control
  // Seconds behind the live edge the user last intentionally chose (updated on
  // every manual seek). The drift watchdog in the HLS effect only auto-corrects
  // drift that grows beyond this baseline (see VideoAnalysis.jsx for the same pattern).
  const baselineDriftRef = useRef(kDefaultLiveDrift);
  const [isBehindLive, setIsBehindLive] = useState(false);
  const [videoFilePath, setVideoFilePath] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [pendingVideoLocation, setPendingVideoLocation] = useState(0);
  const [videoheight, setVideoHeight] = useState(0);
  const videoref = useRef();
  const [comment, setComment] = useState(null);
  const [thisDayTimeCode, setThisDayTimeCode] = useState(dayTimeCode());
  const [thisShare, setThisShare] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Collapsed by default -- the description/comments/tags panel isn't
  // needed to just watch a playlist, so stay out of the way until opened.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [playlistTitle, setPlaylistTitle] = useState("");
  // Only stack video-on-top/list-below on small, portrait screens (phones);
  // every other size keeps the sidebar layout (list left, video right).
  const [stackVertical, setStackVertical] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px) and (orientation: portrait)");
    const update = () => setStackVertical(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const basictags = [
    "middles",
    "outsides",
    "liberos",
    "setters",
    "opposites",
    "passing",
    "spiking",
    "serving",
    "blocking",
    "defending",
  ];
  const [allTags, setAllTags] = useState(null);
  const [, forceUpdate] = useState(0);
  const [isEditable, setIsEditable] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showPlaylistShareModal, setShowPlaylistShareModal] = useState(false);
  const [showTimingModal, setShowTimingModal] = useState(false);

  const useFocus = () => {
    const htmlElRef = useRef(null);
    const setFocus = () => {
      htmlElRef.current && htmlElRef.current.focus();
    };

    return [htmlElRef, setFocus];
  };
  const [tagsRef, setTagsFocus] = useFocus();

  // Returns the current HLS live edge in seconds, or null if it can't be
  // determined yet (used by the drift watchdog, seekToAndPlay, hlsReady, goLive).
  const getHlsLiveEdge = () => {
    const vid = hlsVideoRef.current;
    const hls = hlsInstanceRef.current;
    if (!vid) return null;
    if (hls && typeof hls.liveSyncPosition === "number") return hls.liveSyncPosition;
    if (vid.seekable && vid.seekable.length > 0) return vid.seekable.end(vid.seekable.length - 1);
    return null;
  };

  // Direct hls.js lifecycle for MediaMTX live streams, mirroring VideoAnalysis.jsx.
  useEffect(() => {
    if (!videoFilePath?.endsWith(".m3u8")) {
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
      return;
    }
    const vid = hlsVideoRef.current;
    if (!vid) return;

    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }
    baselineDriftRef.current = kDefaultLiveDrift;

    if (!Hls.isSupported()) {
      // Safari: native HLS support — just set src directly.
      vid.src = videoFilePath;
    } else {
      const hls = new Hls({
        backBufferLength: 7200,
        // Use a very large latency window so hls.js never auto-jumps to the
        // live edge after a historical seek. Must satisfy the hls.js invariant:
        // liveMaxLatencyDuration > liveSyncDuration.
        liveSyncDuration: 5,
        liveMaxLatencyDuration: 86400,
        // Never send credentials: the MediaMTX HLS relay needs no auth and serves
        // its session via a `?session=` query param. A credentialed cross-origin
        // request forces MediaMTX down its `SameSite=None; Secure` cookie path,
        // which cannot round-trip over plain HTTP on the LAN → 401 Unauthorized.
        xhrSetup: (xhr) => {
          xhr.withCredentials = false;
        },
      });
      hls.loadSource(videoFilePath);
      hls.attachMedia(vid);
      hlsInstanceRef.current = hls;
    }

    // See VideoAnalysis.jsx for the rationale: liveMaxLatencyDuration is huge so
    // hls.js never auto-snaps to the live edge (would fight a historical seek).
    // This watchdog instead auto-corrects drift that grows beyond whatever offset
    // from live the user last intentionally chose (baselineDriftRef).
    const driftCheck = setInterval(() => {
      const v = hlsVideoRef.current;
      if (!v || v.paused) return;
      const edge = getHlsLiveEdge();
      if (edge == null) return;
      const drift = edge - v.currentTime;
      const extra = drift - baselineDriftRef.current;
      if (extra > kAutoCatchupExtra) {
        const target = Math.max(0, edge - baselineDriftRef.current);
        v.currentTime = target;
        v.play().catch(() => {});
        setIsBehindLive(false);
      } else {
        setIsBehindLive(drift > kLiveDriftThreshold);
      }
    }, 5000);

    return () => {
      clearInterval(driftCheck);
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoFilePath]);

  const getVideoWindowSize = (width, height) => {
    var w = width;
    var h = width * 0.5625;
    return { width: w + "px", height: h + "px" };
  };

  // Compute the video seek position using the same logic as VideoAnalysis.doSelectEvent:
  // re-reads timing from localStorage so changes to timing settings are reflected immediately.
  // Falls back to the pre-baked videoPosition for old playlists that lack raw fields.
  const computePlayPosition = (pl) => {
    if (pl.eventType != null && (pl.timeStampSeconds != null || pl.rawVideoPosition != null)) {
      const tm = getTimingForEvent({ EventType: pl.eventType });
      if (
        pl.timeStampSeconds != null &&
        pl.videoStartTimeSeconds != null &&
        pl.videoOffset != null &&
        pl.videoOffset >= 0
      ) {
        return pl.timeStampSeconds - pl.videoStartTimeSeconds + pl.videoOffset - tm.leadTime;
      } else if (pl.rawVideoPosition != null) {
        return pl.rawVideoPosition - tm.leadTime;
      }
    }
    // Backward-compatible: old playlist items without raw fields
    return pl.videoPosition;
  };

  const doSelectItem = (pl) => {
    setSelectedItem(pl);
    const pos = computePlayPosition(pl);
    const normalizedUrl = toHlsStreamUrl(pl.videoOnlineUrl);
    if (normalizedUrl !== videoFilePath) {
      setIsReady(false);
      setVideoFilePath(normalizedUrl);
      setPendingVideoLocation(pos);
    } else {
      goToVideoPosition(pos);
    }
  };

  // Mirrors VideoAnalysis.jsx's seekToAndPlay: routes the seek to the raw HLS
  // <video> element (updating the intentional-drift baseline) when playing a
  // MediaMTX live stream, otherwise bypasses the ReactPlayer wrapper so the
  // underlying player resumes playback even if it had been paused.
  const goToVideoPosition = (videoTime) => {
    if (videoTime == null) return;
    const pos = Math.max(0, videoTime);
    if (!isFinite(pos)) return;

    if (videoFilePath?.endsWith(".m3u8")) {
      const vid = hlsVideoRef.current;
      if (vid) {
        vid.currentTime = pos;
        const edge = getHlsLiveEdge();
        if (edge != null) baselineDriftRef.current = Math.max(0, edge - pos);
        vid.play().catch(() => {});
      }
      return;
    }

    if (videoFilePath?.includes("youtube")) {
      const yt = playerRef.current?.getInternalPlayer();
      if (yt?.seekTo) {
        yt.seekTo(pos, /* allowSeekAhead */ true);
        yt.playVideo?.();
      }
      return;
    }

    const vid = playerRef.current?.getInternalPlayer();
    if (vid && vid.currentTime !== undefined) {
      vid.currentTime = pos;
      vid.play().catch(() => {});
    } else {
      playerRef.current?.seekTo(pos, "seconds");
    }
  };

  const playerReady = () => {
    if (!isReady) {
      setIsReady(true);
      playerRef.current.seekTo(pendingVideoLocation, "seconds");
    }
  };

  // Equivalent of playerReady for the raw HLS <video> element (live streams).
  const hlsReady = () => {
    const vid = hlsVideoRef.current;
    if (!vid || !vid.paused) return; // already playing
    setIsReady(true);
    const pos = Math.max(0, pendingVideoLocation || 0);
    vid.currentTime = pos;
    const edge = getHlsLiveEdge();
    if (edge != null) baselineDriftRef.current = Math.max(0, edge - pos);
    vid.play().catch(() => {});
  };

  // Jumps the HLS <video> to the live edge (see the drift-detection watchdog
  // in the HLS effect + the "Go Live" control that calls this).
  const goLive = () => {
    const vid = hlsVideoRef.current;
    if (!vid) return;
    const edge = getHlsLiveEdge();
    if (edge == null) return;
    vid.currentTime = edge;
    baselineDriftRef.current = kDefaultLiveDrift;
    setIsBehindLive(false);
    vid.play().catch(() => {});
  };

  const doTiming = () => {
    setShowTimingModal(true);
  };

  const doCloseTiming = () => {
    setShowTimingModal(false);
  };

  const moveToItem = (pos) => {
    var n = playlists.indexOf(selectedItem);
    n += pos;
    if (n === -1) {
      n = playlists.length - 1;
    } else if (n === playlists.length) {
      n = 0;
    }
    doSelectItem(playlists[n]);
  };

  const onCommentChange = (e) => {
    setComment(e.target.value);
  };

  const doSaveComment = () => {
    selectedItem.comment = comment;
    setComment("");
  };

  const doCommentClicked = (pl) => {
    const cm = pl.comment !== undefined ? pl.comment : "";
    setComment(cm);
    setShowCommentModal(true);
  };

  const doRemoveItem = (pl) => {
    confirmAlert({
      title: tr("Remove Item"),
      message: tr("Do you really want to remove the selected item?"),
      buttons: [
        {
          label: tr("Yes"),
          onClick: () => {
            const idx = playlists.indexOf(pl);
            playlists.splice(idx, 1);
            if (selectedItem === pl) {
              setSelectedItem(null);
            }
            forceUpdate((n) => !n);
          },
        },
        {
          label: tr("No"),
          onClick: () => {},
        },
      ],
    });
  };

  const doSavePlaylist = () => {
    const desc = document.getElementById("playlistdescription").value;
    const comments = document.getElementById("playlistcomments").value;
    var tags = [];
    for (var selectedTag of (selectedTags ?? [])) {
      tags.push(selectedTag.value);
    }
    const descx = desc.replace(/(" ")/gm, "_");
    const fn = dayTimeCode() + "_" + desc + ".playlist";
    saveToPC(
      JSON.stringify({
        events: playlists,
        description: desc,
        comments: comments,
        tags: tags,
      }),
      fn
    );
  };

  const doSharePlaylist = async () => {
    const playlistdescription = document.getElementById(
      "playlistdescription"
    ).value;
    const playlistcomments = document.getElementById("playlistcomments").value;
    const tags = (selectedTags ?? []).map((t) => t.value);
    if (playlistdescription === "") {
      toast.error(tr("Description is required"));
      return;
    }

    setThisDayTimeCode(dayTimeCode());
    setShowPlaylistShareModal(true);
  };

  const doDoShare = async (share) => {
    setShowPlaylistShareModal(false);
    if (!share) {
      return;
    }
    setThisShare(share);
    const playlistdescription = document.getElementById(
      "playlistdescription"
    ).value;
    const playlistcomments = document.getElementById("playlistcomments").value;
    const tags = (selectedTags ?? []).map((t) => t.value);
    var stags = "";
    for (var t of tags) {
      if (stags.length > 0) stags += "|";
      stags += t;
    }
    if (playlistdescription === "") {
      toast.error(tr("Description is required"));
      return;
    }
    const now = new Date();
    const xpl = myzip(JSON.stringify(playlists));
    const pl = {
      id: playlistId,
      description: playlistdescription,
      comments: playlistcomments,
      tags: stags,
      playlists: xpl,
      appName: "VBLive",
      serverName: serverName,
      dateInSeconds: now.getTime() / 1000,
      shareStatus: share.shareStatus,
      shareUsers: share.shareUsers,
    };
    const newplid = await storePlaylist(pl);
    if (newplid > 0) {
      setPlaylistId(newplid);
      toast.success(tr("Playlist saved successfully"));
      if (share.notifyByEmail && share.shareStatus === 2) {
        const recipients = Array.isArray(share.shareUsers)
          ? share.shareUsers
          : String(share.shareUsers ?? "").split(",");
        sendShareNotification({
          to: recipients,
          itemType: "playlist",
          itemTitle: playlistdescription,
          sharedByEmail: serverName,
        });
      }
    } else {
      toast.error(tr("Error saving playlist"));
    }
    forceUpdate((n) => !n);
  };

  const getEventString = (item) => {
    if (item.eventStringColor) {
      return item.eventStringColor + " px-2 font-bold text-shadow";
    } else {
      return "font-bold mt-1";
    }
  };

  const handleSelectTags = (e) => {
    setSelectedTags(e);
    forceUpdate((n) => !n);
  };

  useEffect(() => {
    if (!allTags) {
      if (playlistFileData) {
        setPlaylistId(0);
        if (playlists === null) {
          const pls = JSON.parse(playlistFileData);
          setPlaylists(pls.events);
          document.getElementById("playlistdescription").value = pls.description
            ? pls.description
            : "";
          setPlaylistTitle(pls.description ? pls.description : "");
          document.getElementById("playlistcomments").value = pls.comments
            ? pls.comments
            : "";
          if (pls.tags) {
            var tgoptions = [];
            for (var tg of pls.tags) {
              tgoptions.push({ value: tg, label: tg });
            }
            setSelectedTags(tgoptions);
          }
        }
        setThisShare({ shareStatus: 0, shareUsers: "" });
        setIsEditable(filename === null);
      } else if (playlist) {
        setIsEditable(playlist.serverName === serverName);
        document.getElementById("playlistdescription").value =
          playlist.description;
        setPlaylistTitle(playlist.description ?? "");
        setPlaylistId(playlist.id);
        setThisShare({
          shareStatus: playlist.shareStatus !== null ? playlist.shareStatus : 0,
          shareUsers: playlist.shareUsers !== null ? playlist.shareUsers : "",
        });
        document.getElementById("playlistcomments").value = playlist.comments;
        var tgs = playlist.tags.split("|");
        var tgoptions = [];
        for (var tg of tgs) {
          tgoptions.push({ value: tg, label: tg });
        }
        setSelectedTags(tgoptions);
        const xpl = myunzip(playlist.playlists);
        if (playlists === null) {
          setPlaylists(JSON.parse(xpl));
        }
      }
      var apops = [];
      for (var p of basictags) {
        apops.push({ value: p, label: p });
      }
      setAllTags(apops);
    }

    if (description) {
      document.getElementById("playlistdescription").value = description;
      setPlaylistTitle(description);
    }
    if (initialTags && initialTags.length > 0 && !selectedTags) {
      var apops = [];
      for (var p of initialTags) {
        apops.push({ value: p, label: p });
      }
      setSelectedTags(apops);
    }

    if (playlists && !selectedItem) {
      doSelectItem(playlists[0]);
    }
  });

  useEffect(() => {}, [showPlaylist, videoFilePath, selectedItem, comment]);

  const onBackClick = () => {
    navigate(-1);
  };

  const doDot = (item) => {
    if (item.eventStringColor) {
      return item.eventStringColor + " size-4 sm:size-5";
    } else {
      return "size-4 sm:size-5";
    }
  };

  const doItemDetails = () => {
    return (
      <>
        {selectedItem ? (
          <div className="flex-col text-xs sm:text-sm text-base-content/80 w-full">
            <div className="flex justify-between">
              <div className="">
                <p className="font-bold px-2 mt-2">{selectedItem.playerName}</p>
              </div>
              <div className="">
                <p className="font-light mt-2">{selectedItem.eventSubstring}</p>
              </div>
            </div>
            <div className="flex mt-1 font-bold">
              <StopIcon className={doDot(selectedItem)} />
              <p className="sm:text-sm text-xs px-2">
                {selectedItem.eventString}
              </p>
              {selectedItem.imaHeightM != null && (
                <span className="badge badge-info badge-xs" title="Jump height">
                  {formatJumpHeightM(selectedItem.imaHeightM)}
                </span>
              )}
              {selectedItem.imaDurationSec != null && (
                <span className="badge badge-ghost badge-xs">
                  {selectedItem.imaDurationSec.toFixed(2)}s
                </span>
              )}
            </div>
          </div>
        ) : (
          <p>Please select an item to play</p>
        )}
      </>
    );
  };

  const doLeftSide = (stacked) => {
    return (
      <>
        <div
          className={
            stacked
              ? "flex flex-col gap-1 h-full border-t border-base-content/10"
              : "flex flex-col gap-1 min-w-[320px] flex-1 min-h-0 border-r border-base-content/10"
          }
        >
          <div className="flex items-center justify-between gap-2 bg-base-300 px-2 py-1">
            <p className="text-sm font-semibold truncate">
              {playlistTitle || "Untitled playlist"}
            </p>
            <button
              type="button"
              onClick={() => setDetailsOpen((o) => !o)}
              className="shrink-0"
            >
              <span className="sr-only">
                {detailsOpen ? "Hide playlist details" : "Show playlist details"}
              </span>
              {detailsOpen ? (
                <ChevronUpIcon className="size-5 text-base-content/70" />
              ) : (
                <ChevronDownIcon className="size-5 text-base-content/70" />
              )}
            </button>
          </div>
          {/* Stays mounted (never unmounted) even while "hidden" — several call
              sites still do a legacy document.getElementById("playlistdescription")
              direct DOM write, which would throw "null is not an object" if this
              were conditionally unmounted instead of just hidden. */}
          <div
            aria-disabled={isEditable === false}
            className={
              (isEditable === false
                ? "flex-col is-disabled p-2 bg-base-200"
                : "flex-col p-2 bg-base-200") + (detailsOpen ? "" : " hidden")
            }
          >
            <input
              id="playlistdescription"
              type="text"
              placeholder="Description"
              className="input input-sm p-1 input-bordered w-full rounded-md mb-1"
              onChange={(e) => setPlaylistTitle(e.target.value)}
            />
            <textarea
              id="playlistcomments"
              placeholder="Comments"
              className="textarea textarea-bordered p-1 w-full h-10 rounded-md"
              style={{ lineHeight: "1.25" }}
            ></textarea>
            <div className="flex gap-2">
              <CreatableSelect
                ref={tagsRef}
                id="selectTags"
                name="selectTags"
                placeholder="Select tags"
                onChange={handleSelectTags.bind(this)}
                className="block w-full border-base-300 shadow-sm focus:border-base-300 focus:ring-base-300 text-xs rounded-md"
                options={allTags}
                value={selectedTags}
                isMulti
              />
            </div>
          </div>
          <div
            className={
              stacked
                ? "overflow-auto flex-1 min-h-0"
                : "overflow-auto min-w-[320px] flex-1 min-h-0 mt-2"
            }
          >
            <PlaylistList
              playlists={playlists}
              selItem={selectedItem}
              onItemSelected={(pl) => doSelectItem(pl)}
              onCommentClicked={(pl) => doCommentClicked(pl)}
              onItemRemoved={(pl) => doRemoveItem(pl)}
            />
          </div>
        </div>
      </>
    );
  };

  const doRightSide = (stacked) => {
    return (
      <>
        <div className={stacked ? "flex-col" : "flex flex-col h-full"}>
          <div
            className={
              stacked
                ? "flex flex-col w-full justify-between"
                : "flex flex-col w-full h-full justify-between"
            }
          >
            <div className="flex gap-2 max-h-16 overflow-auto">
              <div className="">
                {selectedItem && selectedItem.comment ? (
                  <ChatBubbleLeftEllipsisIcon className="w-6 h6 text-success" />
                ) : (
                  <ChatBubbleLeftEllipsisIconOutline className="w-6 h6 text-base-content/50" />
                )}
              </div>
              <p
                className="text-sm font-medium"
                style={{ whiteSpace: "pre-line" }}
              >
                {selectedItem && selectedItem.comment}
              </p>
            </div>
            <div
              className={
                (stacked ? "w-full aspect-video bg-black" : "w-full flex-1 min-h-0 bg-black") +
                " relative"
              }
            >
              {selectedItem && selectedItem.imaHeightM != null && (
                <div className="absolute top-2 left-1/2 z-10 -translate-x-1/2 pointer-events-none rounded bg-black/60 px-4 py-2 text-center leading-tight text-white">
                  <div className="text-base font-extrabold uppercase">
                    {selectedItem.playerName}
                    {selectedItem.eventString && ` - ${selectedItem.eventString}`}
                  </div>
                  <div className="text-sm font-extrabold">
                    {formatJumpHeightM(selectedItem.imaHeightM)}
                    {selectedItem.imaDurationSec != null &&
                      ` - Hang Time ${selectedItem.imaDurationSec.toFixed(2)} s`}
                  </div>
                </div>
              )}
              {videoFilePath && videoFilePath.includes("youtube") ? (
                <ReactPlayer
                  ref={playerRef}
                  url={videoFilePath}
                  playing={true}
                  width="100%"
                  height="100%"
                  controls={true}
                  onReady={() => playerReady()}
                />
              ) : videoFilePath?.endsWith(".m3u8") ? (
                <video
                  ref={hlsVideoRef}
                  controls
                  onCanPlay={() => hlsReady()}
                  className="w-full h-full"
                  crossOrigin="anonymous"
                />
              ) : (
                <ReactPlayer
                  ref={playerRef}
                  url={videoFilePath}
                  playing={true}
                  width="100%"
                  height="100%"
                  controls={true}
                  onReady={() => playerReady()}
                />
              )}
            </div>
            {videoFilePath?.endsWith(".m3u8") && isBehindLive && (
              <div className="flex justify-center mt-1">
                <a
                  data-tooltip-id="tt-golive"
                  data-tooltip-content={tr("Playback has fallen behind the live broadcast — click to jump to now.")}
                >
                  <button
                    type="button"
                    className="btn btn-error btn-sm w-28 gap-1 whitespace-nowrap flex-shrink-0 px-3 animate-pulse"
                    onClick={goLive}
                  >
                    <SignalIcon className="h-4 w-4" />
                    {tr("Go Live")}
                  </button>
                </a>
                <Tooltip
                  id="tt-golive"
                  place={"bottom"}
                  style={{
                    backgroundColor: "hsl(var(--b3))",
                    color: "hsl(var(--bc))",
                  }}
                />
              </div>
            )}
            <div className="flex items-center justify-between px-2">
              <p className="text-xs mt-1 ml-0">{videoFilePath}</p>
              <a
                data-tooltip-id="tt-timing"
                data-tooltip-content={tr("Set timing for events")}
              >
                <ClockIcon
                  className="btn-toolbar"
                  onClick={() => doTiming()}
                />
              </a>
              <Tooltip
                id="tt-timing"
                place={"bottom-end"}
                style={{
                  backgroundColor: "hsl(var(--b3))",
                  color: "hsl(var(--bc))",
                }}
              />
            </div>
            <div className="flex flex-col">
              {comments &&
                comments.map((cm, i) => (
                  <div className="">
                    {i % 2 === 0 ? (
                      <div className="chat chat-start">
                        <div className="chat-bubble">{cm}</div>
                      </div>
                    ) : (
                      <div className="chat chat-end">
                        <div className="chat-bubble">{cm}</div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </>
    );
  };

  const header = (
    <div className="flex h-14 items-center gap-x-4 border-b border-gray-200 bg-base-300 px-2 shadow-sm sm:gap-x-6 sm:px-4 lg:px-2">
      {!stackVertical && (
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="-m-2.5 p-2.5 text-base-content/50 lg:hidden"
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon
            aria-hidden="true"
            className="size-6 cursor-pointer text-base-content/50 hover:text-base-content/80"
          />
        </button>
      )}
      <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6 lg:ml-8">
        <form action="#" method="GET" className="grid flex-1 grid-cols-1">
          <div className="flex justify-between">
            {doItemDetails()}

            <div className="flex ml-4">
              <a
                data-tooltip-id="tt-prev"
                data-tooltip-content="Previous item"
              >
                <button type="button" className="mt-3.5">
                  <span className="sr-only">Navigate Back</span>
                  <ChevronLeftIcon
                    className="size-6 cursor-pointer text-base-content/50 hover:text-base-content/80"
                    onClick={() => moveToItem(-1)}
                  />
                </button>
              </a>
              <Tooltip
                id="tt-prev"
                place={"bottom-end"}
                style={{
                  backgroundColor: "hsl(var(--b3))",
                  color: "hsl(var(--bc))",
                }}
              />

              <a
                data-tooltip-id="tt-prev"
                data-tooltip-content="Next item"
              >
                <button type="button" className="mt-3.5">
                  <span className="sr-only">Navigate Back</span>
                  <ChevronRightIcon
                    className="size-6 cursor-pointer text-base-content/50 hover:text-base-content/80"
                    onClick={() => moveToItem(1)}
                  />
                </button>
              </a>
              <Tooltip
                id="tt-prev"
                place={"bottom-end"}
                style={{
                  backgroundColor: "hsl(var(--b3))",
                  color: "hsl(var(--bc))",
                }}
              />
            </div>
          </div>
        </form>
        <div className="flex items-center gap-x-4 lg:gap-x-6">
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => onBackClick()}
          >
            {tr("Close")}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {stackVertical ? (
        <div className="flex flex-col h-screen">
          <div className="sticky top-0 z-40 shrink-0">{header}</div>

          {/* Video on top, full width */}
          <div className="shrink-0 w-full">{doRightSide(true)}</div>

          {/* Event list below, fills the remaining height, scrollable */}
          <div className="flex-1 min-h-0">{doLeftSide(true)}</div>
        </div>
      ) : (
        <div className="">
          <div>
            <Dialog
              open={sidebarOpen}
              onClose={setSidebarOpen}
              className="relative z-50 lg:hidden"
            >
              <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
              />

              <div className="fixed inset-0 flex">
                <DialogPanel
                  transition
                  className="relative mr-16 flex w-full max-w-[320px] flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
                >
                  <TransitionChild>
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                      <button
                        type="button"
                        onClick={() => setSidebarOpen(false)}
                        className="-m-2.5 p-2.5"
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          aria-hidden="true"
                          className="size-6 text-white"
                        />
                      </button>
                    </div>
                  </TransitionChild>
                  {/* Sidebar component, swap this element with another sidebar if you like */}
                  <div className="flex grow flex-col min-h-0 bg-base-100">
                    {doLeftSide(false)}
                  </div>
                </DialogPanel>
              </div>
            </Dialog>

            {/* Static sidebar for desktop */}
            <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-[320px] lg:flex-col">
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col min-h-0 bg-base-100">{doLeftSide(false)}</div>
            </div>

            <div className="lg:px-4 lg:ml-72 flex flex-col h-screen">
              <div className="sticky top-0 z-40 shrink-0">
                {header}
              </div>

              <main className="py-2 flex-1 min-h-0 flex flex-col">
                <div className="flex-1 min-h-0 flex flex-col px-1 sm:pl-0 sm:pr-2 lg:pr-2 lg:pl-8">
                  {doRightSide(false)}
                </div>
              </main>
            </div>
          </div>
        </div>
      )}

      <div>
        {showCommentModal && (
          <dialog className="modal z-[9999]" open onKeyDown={(e) => { if (e.key === "Escape") setShowCommentModal(false); }}>
            <div className="modal-box sm:w-4/12 w-full max-w-5xl h-[50vh]">
              <h3 className="mb-4 font-bold text-2xl">Comment</h3>
              <div className="flex flex-col">
                <div>
                  <textarea
                    placeholder="Type comment here"
                    value={comment}
                    style={{ whiteSpace: "pre-line" }}
                    className="textarea textarea-bordered w-full h-40"
                    onChange={onCommentChange}
                  />
                </div>
                <div className="flex justify-end">
                  <div className="modal-action">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => setShowCommentModal(false)}
                    >
                      Close
                    </button>
                  </div>
                  <div className="modal-action">
                    <button
                      className="btn btn-sm btn-info ml-4"
                      onClick={() => { doSaveComment(); setShowCommentModal(false); }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <form method="dialog" className="modal-backdrop" onClick={() => setShowCommentModal(false)} />
          </dialog>
        )}

        {showPlaylistShareModal && (
          <dialog className="modal z-[9999]" open onKeyDown={(e) => { if (e.key === "Escape") setShowPlaylistShareModal(false); }}>
            <div className="modal-box sm:w-4/12 w-full max-w-5xl min-w-[480px] h-[70vh] shadow rounded-none">
              <h3 className="mb-4 font-bold text-2xl">Share</h3>
              <div className="flex flex-col">
                <div>
                  <Share
                    shareStatus={thisShare && thisShare.shareStatus}
                    shareUsers={thisShare && thisShare.shareUsers}
                    currentTime={thisDayTimeCode}
                    onShare={(share) => doDoShare(share)}
                  />
                </div>
              </div>
            </div>
            <form method="dialog" className="modal-backdrop" onClick={() => setShowPlaylistShareModal(false)} />
          </dialog>
        )}

        {showTimingModal && (
          <dialog className="modal z-[9999]" open onKeyDown={(e) => { if (e.key === "Escape") setShowTimingModal(false); }}>
            <div className="modal-box sm:w-4/12 w-full max-w-5xl h-[70vh]">
              <h3 className="mb-4 font-bold text-2xl">{tr("Timing")}</h3>
              <div className="flex flex-col">
                <div>
                  <Timing onClose={() => doCloseTiming()} />
                </div>
              </div>
            </div>
            <form method="dialog" className="modal-backdrop" onClick={() => setShowTimingModal(false)} />
          </dialog>
        )}
      </div>
    </>
  );
}

export default Playlist;
